import MuleBadge from '../../../../components/MuleBadge';
import React from 'react';
import Lottie from 'lottie-react';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { config } from '../../../../config/config';
import { VehicleMarker } from '../types';
import loadingAnimation from './loadingAnimation.json';
import { HosClockDisplay } from './HosClockDisplay';
import { DriverLane } from '../../../../components/driver-status-monitor';
import { DEFAULT_DISPLAY_SETTINGS } from '../../../../components/driver-status-monitor';
import type { DriverWithMoves } from '../../../../components/driver-status-monitor';
import Toggle from '../../../../components/Toggle';

interface VehicleInfoDrawerProps {
  selectedVehicle: VehicleMarker | null | undefined;
  setSelectedVehicleId: (id: string | null) => void;
  setIsInfoWindowMinimized: (value: boolean) => void;
  loadingDispatches: boolean;
  currentDispatches: any;
  routeInfo: { distance: string; duration: string; eta?: string } | null;
  selectedDispatchRoute: any;
  setSelectedDispatchRoute: (route: any) => void;
  isCollapsed: boolean;
  setIsCollapsed: (collapsed: boolean) => void;
  availablePros: any[];
  setIsProDrawerOpen: (open: boolean) => void;
  setSelectedProClusterList: (clusterList: any[][] | null) => void;
  setIsViewingAllVisible: (viewing: boolean) => void;
  visibleProClusters: any[][];
  onParkingLocationClick?: (coordinates: string, geofencetype: string, geofenceradius: number, name: string) => void;
  // New props for DriverLane
  driverMovesData: DriverWithMoves | null;
  loadingDriverMoves: boolean;
  showCompletedMoves: boolean;
  setShowCompletedMoves: (show: boolean) => void;
  onMoveProClick: (pronumber: number, legnum: number) => void;
  onMoveProDetailsClick: (pronumber: number) => void;
}

export const VehicleInfoDrawer: React.FC<VehicleInfoDrawerProps> = ({
  selectedVehicle,
  setSelectedVehicleId,
  setIsInfoWindowMinimized,
  loadingDispatches,
  currentDispatches,
  routeInfo,
  selectedDispatchRoute,
  setSelectedDispatchRoute,
  isCollapsed,
  setIsCollapsed,
  availablePros,
  setIsProDrawerOpen,
  setSelectedProClusterList,
  setIsViewingAllVisible,
  visibleProClusters,
  onParkingLocationClick,
  driverMovesData,
  loadingDriverMoves,
  showCompletedMoves,
  setShowCompletedMoves,
  onMoveProClick,
  onMoveProDetailsClick,
}) => {
  if (!selectedVehicle) return null;

  // Get comma-separated list of PRO numbers from current dispatches
  const proNumbers = currentDispatches?.dispatches
    ?.map((dispatch: any) => dispatch.pronumber)
    .filter((pro: any, index: number, self: any[]) => pro && self.indexOf(pro) === index) // Remove duplicates and nulls
    .join(',') || '';

  const cmsUrl = proNumbers
    ? `${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${proNumbers}&searchtype=pronumber`
    : `${config.LEGACY_CMS_URL.replace('cms', 'safetynew')}/index_safety.pl?action=savecustomsearch&searchtype=truckvin&contlist=${selectedVehicle.vin}`;

  return (
    <div className="h-full flex flex-col bg-panel border-r border-gray-200 dark:border-gray-700 relative">
      {/* Collapse/Expand Handle - positioned to align with header center */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-7 top-[12px] z-20 bg-panel border border-l-0 border-gray-200 dark:border-gray-700 rounded-r-lg py-2 px-1.5 hover:bg-highlight transition-colors shadow-sm"
        aria-label={isCollapsed ? "Expand Current Dispatches" : "Collapse Current Dispatches"}
      >
        <ChevronLeftIcon
          className={`w-4 h-4 text-gray-600 dark:text-gray-400 ${
            isCollapsed ? 'rotate-180' : 'rotate-0'
          }`}
        />
      </button>

      {/* Header */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <h3 className="text-lg font-medium leading-6 text-heading">
          <a
            href={cmsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline flex items-center gap-1.5"
            title={proNumbers ? "Click to view dispatches in CMS" : "Click to view in Safety System"}
          >
            <span>
              {selectedVehicle.dispatchInfo?.unitNumber && selectedVehicle.dispatchInfo?.driverName
                ? `${selectedVehicle.dispatchInfo.unitNumber} - ${selectedVehicle.dispatchInfo.driverName}`
                : selectedVehicle.dispatchInfo?.driverName || selectedVehicle.dispatchInfo?.unitNumber || selectedVehicle.name}<MuleBadge isMule={selectedVehicle.dispatchInfo?.isMule} />
            </span>
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          </a>
        </h3>
        <button
          type="button"
          onClick={() => {
            setSelectedVehicleId(null);
            setIsInfoWindowMinimized(false);
            setSelectedDispatchRoute(null);
          }}
          className="rounded-md text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <span className="sr-only">Close</span>
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* HOS Clock Display */}
      <HosClockDisplay
        currentDutyStatus={currentDispatches?.currentDutyStatus}
        timeInCurrentStatus={currentDispatches?.timeInCurrentStatus}
        breakTimeRemaining={currentDispatches?.breakTimeRemaining}
        driveTimeRemaining={currentDispatches?.driveTimeRemaining}
        shiftTimeRemaining={currentDispatches?.shiftTimeRemaining}
        cycleTimeRemaining={currentDispatches?.cycleTimeRemaining}
        samsaraDriverId={currentDispatches?.samsaraDriverId}
        parkingLocationName={currentDispatches?.parkingLocation?.coordinates ? currentDispatches.parkingLocation.name : null}
        onParkingLocationClick={
          currentDispatches?.parkingLocation?.coordinates
            ? () => onParkingLocationClick?.(
                currentDispatches.parkingLocation.coordinates,
                currentDispatches.parkingLocation.geofencetype || 'CIRCULAR',
                currentDispatches.parkingLocation.geofenceradius || 0.5,
                currentDispatches.parkingLocation.name
              )
            : undefined
        }
      />

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          {/* Out Of Service Status - Show when driver is OOS */}
          {selectedVehicle.dispatchInfo?.unitNumber && selectedVehicle.dispatchInfo?.v1Status?.toUpperCase() === 'OUT OF SERVICE' && !loadingDispatches && currentDispatches && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <div className="text-center mb-3">
                <div className="text-lg font-semibold text-red-600 dark:text-red-400">
                  Out Of Service
                </div>
              </div>
              <div className="space-y-2 text-sm">
                {currentDispatches.oosType && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Type:</span>
                    <span className="font-medium text-body">{currentDispatches.oosType}</span>
                  </div>
                )}
                {currentDispatches.oosDate && !isNaN(new Date(currentDispatches.oosDate).getTime()) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Date:</span>
                    <span className="font-medium text-body">
                      {new Date(currentDispatches.oosDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                )}
                {currentDispatches.estReturnDate && !isNaN(new Date(currentDispatches.estReturnDate).getTime()) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Est. Return:</span>
                    <span className="font-medium text-body">
                      {new Date(currentDispatches.estReturnDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                )}
                {currentDispatches.oosComments && (
                  <div className="mt-3 pt-3 border-t border-red-200 dark:border-red-800">
                    <div className="text-gray-600 dark:text-gray-400 mb-1">Comments:</div>
                    <div className="text-body whitespace-pre-wrap">{currentDispatches.oosComments}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Available For Work - Show when no remaining dispatches and NOT out of service */}
          {selectedVehicle.dispatchInfo?.unitNumber && (!selectedVehicle.dispatchInfo?.remainingDispatchesToday || selectedVehicle.dispatchInfo.remainingDispatchesToday === 0) && !loadingDispatches && selectedVehicle.dispatchInfo?.v1Status?.toUpperCase() !== 'OUT OF SERVICE' && (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="text-lg font-semibold text-green-600 dark:text-green-400">
                  Available For Work
                </div>
                {(!currentDispatches || currentDispatches.dispatchCount === 0) && (
                  <div className="text-sm text-muted mt-2">
                    No dispatches scheduled for today
                  </div>
                )}
              </div>
            </div>
          )}

          {/* See All Pros In View Button - Show when there are visible pros */}
          {availablePros.length > 0 && visibleProClusters.length > 0 && (
            <div className="px-4 pb-4">
              <button
                onClick={() => {
                  setSelectedProClusterList(visibleProClusters);
                  setIsViewingAllVisible(true);
                  setIsProDrawerOpen(true);
                }}
                className="w-full px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm rounded-lg transition-colors shadow-sm flex items-center justify-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                {visibleProClusters.flat().length} Pros In View
              </button>
            </div>
          )}

          {/* Driver Lane - replaces old dispatch cards */}
          {selectedVehicle.dispatchInfo?.unitNumber && (
            <div>
              {loadingDriverMoves ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <Lottie animationData={loadingAnimation} loop={true} style={{ width: 60, height: 60 }} />
                  <div className="text-xs text-muted text-center mt-2">Loading moves...</div>
                </div>
              ) : driverMovesData && driverMovesData.moves.length > 0 ? (
                <>
                  {/* Show Completed Toggle */}
                  {driverMovesData.moves.some(m => m.status === 'Completed') && (
                    <div className="flex items-center justify-end gap-2 mb-2">
                      <Toggle
                        checked={showCompletedMoves}
                        onChange={setShowCompletedMoves}
                        size="sm"
                      />
                      <span className="text-xs text-muted">Show Completed</span>
                    </div>
                  )}
                  <DriverLane
                    driver={driverMovesData}
                    hideHeader
                    showCompleted={showCompletedMoves}
                    displaySettings={DEFAULT_DISPLAY_SETTINGS}
                    orientation="vertical"
                    onProClick={onMoveProClick}
                    onProDetailsClick={onMoveProDetailsClick}
                  />
                </>
              ) : !loadingDispatches && (!currentDispatches || currentDispatches.dispatchCount === 0) ? (
                <div className="text-xs text-muted text-center py-2">No dispatches for today</div>
              ) : null}
            </div>
          )}
        </div>
    </div>
  );
};
